/**
 * SourcingForm — "tell us what you're looking for and your ceiling price." The
 * spec is a SearchFilters partial (validated against the same zod schema
 * /api/search uses); the limit is a ceiling per-carat or total. Minimal: shape /
 * carat / colour / clarity + the limit. Shared by both shells.
 */
import { useState } from 'react'
import { SHAPES_CORE, COLORS_CORE, CLARITIES_CORE } from '../../lib/filterHelpers'
import type { SearchFilters } from '../../api/types'
import type { SourcingLimitBasis } from '../../lib/sourcing'

interface Props {
  placing: boolean
  onPlace: (args: { spec: SearchFilters; limitBasis: SourcingLimitBasis; limitValue: number; certifiedOnly: boolean }) => void
  maxOpenReached?: boolean
}

function Chip({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
        active ? 'bg-gold text-white' : 'bg-navy/5 text-ink hover:bg-navy/10'
      }`}
    >
      {children}
    </button>
  )
}

export function SourcingForm({ placing, onPlace, maxOpenReached }: Props) {
  const [shape, setShape] = useState<string | null>(null)
  const [color, setColor] = useState<string[]>([])
  const [clarity, setClarity] = useState<string[]>([])
  const [caratMin, setCaratMin] = useState('')
  const [caratMax, setCaratMax] = useState('')
  const [basis, setBasis] = useState<SourcingLimitBasis>('total')
  const [limit, setLimit] = useState('')
  const [certifiedOnly, setCertifiedOnly] = useState(true)
  const [touched, setTouched] = useState(false)

  const toggle = (list: string[], set: (v: string[]) => void, item: string) =>
    set(list.includes(item) ? list.filter((v) => v !== item) : [...list, item])

  const limitNum = parseFloat(limit)
  const validLimit = Number.isFinite(limitNum) && limitNum > 0
  const canPlace = validLimit && !placing && !maxOpenReached

  function submit() {
    setTouched(true)
    if (!validLimit) return
    const spec: SearchFilters = {}
    if (shape) spec.shape = [shape]
    if (color.length) spec.color = color
    if (clarity.length) spec.clarity = clarity
    const cMin = parseFloat(caratMin); if (Number.isFinite(cMin)) spec.caratMin = cMin
    const cMax = parseFloat(caratMax); if (Number.isFinite(cMax)) spec.caratMax = cMax
    onPlace({ spec, limitBasis: basis, limitValue: limitNum, certifiedOnly })
  }

  return (
    <div className="space-y-5">
      {/* Shape */}
      <div className="space-y-2">
        <h3 className="text-sm font-semibold text-ink">Shape <span className="font-normal text-soft">· optional</span></h3>
        <div className="flex flex-wrap gap-2">
          {SHAPES_CORE.map((s) => (
            <Chip key={s} active={shape === s} onClick={() => setShape(shape === s ? null : s)}>{s}</Chip>
          ))}
        </div>
      </div>

      {/* Carat */}
      <div className="space-y-2">
        <h3 className="text-sm font-semibold text-ink">Carat <span className="font-normal text-soft">· optional</span></h3>
        <div className="flex gap-2">
          <input type="number" step="0.01" inputMode="decimal" value={caratMin} onChange={(e) => setCaratMin(e.target.value)}
            placeholder="Min" aria-label="Minimum carat"
            className="flex-1 min-w-0 px-3 py-2 rounded-lg border border-border bg-white text-ink placeholder:text-soft focus:outline-none focus:ring-2 focus:ring-gold/30" />
          <input type="number" step="0.01" inputMode="decimal" value={caratMax} onChange={(e) => setCaratMax(e.target.value)}
            placeholder="Max" aria-label="Maximum carat"
            className="flex-1 min-w-0 px-3 py-2 rounded-lg border border-border bg-white text-ink placeholder:text-soft focus:outline-none focus:ring-2 focus:ring-gold/30" />
        </div>
      </div>

      {/* Colour */}
      <div className="space-y-2">
        <h3 className="text-sm font-semibold text-ink">Colour <span className="font-normal text-soft">· optional</span></h3>
        <div className="flex flex-wrap gap-2">
          {COLORS_CORE.map((c) => (
            <Chip key={c} active={color.includes(c)} onClick={() => toggle(color, setColor, c)}>{c}</Chip>
          ))}
        </div>
      </div>

      {/* Clarity */}
      <div className="space-y-2">
        <h3 className="text-sm font-semibold text-ink">Clarity <span className="font-normal text-soft">· optional</span></h3>
        <div className="flex flex-wrap gap-2">
          {CLARITIES_CORE.map((c) => (
            <Chip key={c} active={clarity.includes(c)} onClick={() => toggle(clarity, setClarity, c)}>{c}</Chip>
          ))}
        </div>
      </div>

      {/* Limit price — the one required field */}
      <div className="space-y-2 rounded-card border border-border bg-paper p-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-ink">Your ceiling price</h3>
          <div className="inline-flex rounded-full bg-navy/5 p-0.5 text-xs font-semibold">
            <button type="button" onClick={() => setBasis('total')}
              className={`px-3 py-1 rounded-full transition-colors ${basis === 'total' ? 'bg-white text-ink shadow-sm' : 'text-soft'}`}>Total</button>
            <button type="button" onClick={() => setBasis('ppc')}
              className={`px-3 py-1 rounded-full transition-colors ${basis === 'ppc' ? 'bg-white text-ink shadow-sm' : 'text-soft'}`}>Per carat</button>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-lg font-semibold text-soft">$</span>
          <input type="number" step="1" inputMode="decimal" value={limit} onChange={(e) => setLimit(e.target.value)}
            placeholder={basis === 'total' ? 'Max total, e.g. 4000' : 'Max $/carat, e.g. 1200'} aria-label="Limit price"
            className="flex-1 min-w-0 px-3 py-2.5 rounded-lg border border-border bg-white text-ink text-lg placeholder:text-base placeholder:text-soft focus:outline-none focus:ring-2 focus:ring-gold/30" />
        </div>
        <p className="text-xs text-soft">We source the cheapest match at or under this — you keep the difference. It’s a ceiling, not a fixed price.</p>
        {touched && !validLimit && <p className="text-xs font-medium text-red-600">Enter a ceiling price above 0.</p>}
      </div>

      {/* Certified toggle */}
      <label className="flex items-center gap-2.5 text-sm text-ink cursor-pointer select-none">
        <input type="checkbox" checked={certifiedOnly} onChange={(e) => setCertifiedOnly(e.target.checked)}
          className="h-4 w-4 rounded border-border text-gold focus:ring-gold/30" />
        Certified stones only (IGI / GIA)
      </label>

      {maxOpenReached && (
        <p className="text-sm font-medium text-amber-700 bg-amber-50 rounded-lg px-3 py-2">
          You’ve reached your open-order limit. Confirm or cancel one to place another.
        </p>
      )}

      <button type="button" onClick={submit} disabled={!canPlace}
        className="w-full px-4 py-3 rounded-full bg-gold text-white font-semibold hover:bg-gold-hover active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100">
        {placing ? 'Placing…' : 'Source this diamond'}
      </button>
    </div>
  )
}
